import React from 'react';
import { Box, useTheme, useMediaQuery } from '@material-ui/core';
import { useCloudAsset } from '../../../../lib/services/cloud-asset.service';

interface CloudImageProps {
  src: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  className?: string;
  containerClassName?: string;
  style?: React.CSSProperties;
  fallbackSrc?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  maxWidthRestrictions?: {
    desktop?: string | number;
    tablet?: string | number;
    mobile?: string | number;
    default?: string | number;
  };
}

export const CloudImage: React.FC<CloudImageProps> = ({
  src,
  alt = '',
  width,
  height,
  className,
  containerClassName,
  style,
  fallbackSrc,
  objectFit = 'contain',
  maxWidthRestrictions
}) => {
  const { url, loading, error } = useCloudAsset(src);
  const [imgError, setImgError] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleError = () => {
    setImgError(true);
  };

  // Determine max-width based on device type and restrictions
  const getMaxWidth = () => {
    if (!maxWidthRestrictions) return undefined;
    
    if (isMobile && maxWidthRestrictions.mobile !== undefined) {
      return maxWidthRestrictions.mobile;
    }
    if (isTablet && !isMobile && maxWidthRestrictions.tablet !== undefined) {
      return maxWidthRestrictions.tablet;
    }
    if (!isTablet && maxWidthRestrictions.desktop !== undefined) {
      return maxWidthRestrictions.desktop;
    }
    return maxWidthRestrictions.default;
  };

  const maxWidth = getMaxWidth();
  const showError = (error || imgError) && !fallbackSrc;
  const imageUrl = error || imgError ? fallbackSrc : url;

  if (loading) {
    return (
      <Box
        className={containerClassName}
        style={{ 
          width, 
          height, 
          maxWidth,
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        <svg style={{ width: '2rem', height: '2rem', color: '#6c757d' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </Box>
    );
  }

  if (showError || !imageUrl) {
    return (
      <Box
        className={containerClassName}
        style={{ 
          width, 
          height,
          maxWidth,
          backgroundColor: '#f8f9fa',
          border: '1px solid #6c757d',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
      >
        <Box style={{ textAlign: 'center', padding: '8px' }}>
          <svg 
            style={{ width: '2rem', height: '2rem', color: '#6c757d', marginBottom: '8px' }}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
          <Box component="p" style={{ fontSize: '0.875rem', color: '#6c757d', margin: 0 }}>
            Image not found
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={containerClassName} style={{ width, height, maxWidth, ...style }}>
      <img
        src={imageUrl}
        alt={alt}
        onError={handleError}
        className={className}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit 
        }}
      />
    </Box>
  );
};